
import { useState, useEffect } from "react";
import { format, parseISO, isBefore, startOfToday, eachDayOfInterval } from "date-fns";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { formatTimeString } from "@/utils/formatTime";

interface AvailabilitySlotsProps {
  slots: Array<{
    date: string;
    endDate?: string;
    slots: string[];
    recurring?: {
      days: string[];
      until: string;
    };
  }>;
  onSlotSelect: (date: string, time: string) => void;
  selectedDate?: string;
  selectedTime?: string;
}

export const AvailabilitySlots = ({
  slots,
  onSlotSelect,
  selectedDate,
  selectedTime
}: AvailabilitySlotsProps) => {
  const [visibleSlots, setVisibleSlots] = useState(3);
  const [selectedValue, setSelectedValue] = useState<string>('');

  // Initialize selectedValue based on props
  useEffect(() => {
    if (selectedDate && selectedTime) {
      setSelectedValue(`${selectedDate}-${selectedTime}`);
    }
  }, [selectedDate, selectedTime]);

  if (!slots || slots.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No available time slots</p>
    );
  }

  // Generate all available dates based on recurring patterns and date ranges
  const availableDates = slots.flatMap(slot => {
    const dates = [];
    const startDate = parseISO(slot.date);
    const endDate = slot.endDate ? parseISO(slot.endDate) : startDate;
    
    if (slot.recurring) {
      // For recurring slots, generate dates up to the 'until' date
      const recEndDate = parseISO(slot.recurring.until);
      const dateRange = eachDayOfInterval({ start: startDate, end: recEndDate });
      
      dateRange.forEach(date => {
        const dayName = format(date, 'EEEE');
        if (slot.recurring?.days.includes(dayName)) {
          dates.push({
            date: format(date, 'yyyy-MM-dd'),
            slots: slot.slots,
          });
        }
      });
    } else {
      // For date range slots, generate all dates in the range
      const dateRange = eachDayOfInterval({ start: startDate, end: endDate });
      
      dateRange.forEach(date => {
        dates.push({
          date: format(date, 'yyyy-MM-dd'),
          slots: slot.slots,
        });
      });
    }
    
    return dates;
  });

  // Flatten and process all slots
  const flattenedSlots = availableDates
    .flatMap(dateSlot => 
      dateSlot.slots.map(timeSlot => ({
        date: dateSlot.date,
        time: timeSlot,
        displayDate: format(parseISO(dateSlot.date), "EEEE, MMMM d, yyyy"),
        value: `${dateSlot.date}-${timeSlot}`
      }))
    )
    .sort((a, b) => parseISO(a.date).getTime() - parseISO(b.date).getTime())
    .filter(slot => !isBefore(parseISO(slot.date), startOfToday())); // Filter out past dates

  const hasMoreSlots = visibleSlots < flattenedSlots.length;

  const handleValueChange = (value: string) => {
    console.log('Raw selected value:', value);
    
    // Find the matching slot from our flattened array
    const selectedSlot = flattenedSlots.find(slot => slot.value === value);
    
    if (selectedSlot) {
      console.log('Found matching slot:', selectedSlot);
      setSelectedValue(value);
      onSlotSelect(selectedSlot.date, selectedSlot.time);
    } else {
      console.error('No matching slot found for value:', value);
    }
  };

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium">Select availability slot</h4>
      <ScrollArea className="rounded-md border p-4">
        <RadioGroup
          value={selectedValue}
          onValueChange={handleValueChange}
          className="grid gap-2"
        >
          {flattenedSlots.slice(0, visibleSlots).map(({ date, time, displayDate, value }) => (
            <div
              key={value}
              className="flex items-center space-x-2 rounded-md border p-3 hover:bg-accent"
            >
              <RadioGroupItem value={value} id={value} />
              <Label htmlFor={value} className="flex-grow cursor-pointer">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
                  <span>{displayDate}</span>
                  <span className="text-muted-foreground">{formatTimeString(time)}</span>
                </div>
              </Label>
            </div>
          ))}
        </RadioGroup>
        
        {hasMoreSlots && (
          <Button
            variant="ghost"
            className="w-full mt-4 flex items-center justify-center gap-2"
            onClick={() => setVisibleSlots(prev => prev + 3)}
          >
            Show additional availability
            <ChevronDown className="h-4 w-4" />
          </Button>
        )}
      </ScrollArea>
    </div>
  );
};
